import { fetchLabelTypes, fetchLabelPdf } from "../api/company";
import { getPdfReport } from "../api/service/report";
import { printWorkOrder } from "../api/work-order";

function labelTypesFromResponse(raw) {
  return Array.isArray(raw) ? raw : raw ? Object.values(raw) : [];
}

/** Prefer API id 1 (90x29 product); Object.values order is not stable. */
function pickProductLabelType(types) {
  return (
    types.find((x) => Number(x?.id) === 1) ||
    types.find((x) => /\b90\s*[×x]\s*29\b/i.test(String(x?.name || ""))) ||
    types[0]
  );
}

/**
 * Opens label PDF in a new tab (product die-cut type id 1 / 90x29 when available).
 */
export async function quickPrintProductLabel(productId, { t } = {}) {
  if (!productId) {
    throw new Error(t ? t("inventory.picking.quick_print.no_product") : "Missing product");
  }

  const typesRes = await fetchLabelTypes();
  const types = labelTypesFromResponse(typesRes?.data?.data);
  if (!types.length) {
    throw new Error(t ? t("inventory.picking.quick_print.no_types") : "No label types");
  }

  const type = pickProductLabelType(types);
  const response = await fetchLabelPdf({
    type_id: type.id,
    product_data: [{ product_id: productId }],
  });

  const pdfUrl = response?.data?.pdf_url;
  if (!pdfUrl) {
    throw new Error(t ? t("inventory.picking.quick_print.failed") : "Could not create PDF");
  }

  window.open(pdfUrl, "_blank", "noopener,noreferrer");
}

/**
 * Quick label from explicit fields (e.g. order / work order / report) — no product.
 * Maps to standard label fields: title, subtitle, description, description_two, qr_code.
 */
export async function quickPrintDocumentLabel(fields, { t } = {}) {
  const typesRes = await fetchLabelTypes();
  const types = labelTypesFromResponse(typesRes?.data?.data);
  if (!types.length) {
    throw new Error(t ? t("inventory.picking.quick_print.no_types") : "No label types");
  }

  const type = pickProductLabelType(types);
  const {
    title = "",
    subtitle = "",
    description = "",
    description_two = "",
    qr_code = "",
  } = fields || {};

  const response = await fetchLabelPdf({
    type_id: type.id,
    product_data: [
      {
        title,
        subtitle,
        description,
        description_two,
        qr_code,
      },
    ],
  });

  const pdfUrl = response?.data?.pdf_url;
  if (!pdfUrl) {
    throw new Error(t ? t("inventory.picking.quick_print.failed") : "Could not create PDF");
  }

  window.open(pdfUrl, "_blank", "noopener,noreferrer");
}

/**
 * Følgeseddel (delivery note) PDF — report and work order use existing print_pdf with type "note".
 * Orders have no packing-slip endpoint in API yet; caller should hide the action for module "order".
 */
export async function quickPrintPackingSlip({ sourceType, sourceId }, { t } = {}) {
  if (!sourceId) {
    throw new Error(t ? t("inventory.picking.delivery_note.missing_id") : "Missing document");
  }

  let response;
  if (sourceType === "report") {
    response = await getPdfReport({
      id: sourceId,
      params: { printReportType: "note" },
    });
  } else if (sourceType === "work_order") {
    response = await printWorkOrder({
      id: sourceId,
      printWorkOrderType: "note",
    });
  } else {
    throw new Error(t ? t("inventory.picking.delivery_note.unsupported") : "Unsupported document type");
  }

  const pdfUrl = response?.data?.pdf_url;
  if (!pdfUrl) {
    throw new Error(t ? t("inventory.picking.delivery_note.failed") : "Could not create PDF");
  }

  window.open(pdfUrl, "_blank", "noopener,noreferrer");
}
